// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type WorkCell, type TaskType, type TaskTypeGroup, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Box, Button } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import type { WithNullable } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';

import { useWorkCellSetup } from '../WorkCellSetupContext';

type Props = {
  taskTypes: TaskType[];
  taskTypeGroups: TaskTypeGroup[];
  onComplete: () => void;
};

type ValidatedFormBody = {
  workCell: WorkCell;
};
type FormBody = WithNullable<ValidatedFormBody>;

const MapTaskFields = ({ taskTypes, taskTypeGroups, onComplete }: Props) => {
  const {
    facility: { facilityId },
    draftShopConfiguration,
    mapWorkCellTaskTypesAndGroups,
    serverSideGridProps: { refreshDetailGrid, expandDetailGrid },
  } = useWorkCellSetup();
  const paginatedGet = useEvolvePaginatedGet('shop/workCell', {
    defaultConfig: {
      params: {
        facilityId,
        excludeUnassigned: true,
        shopConfigurationId: draftShopConfiguration?.shopConfigurationId,
      },
    },
  });
  const form = useForm<FormBody>({
    initialValues: {
      workCell: null,
    },
    validate: {
      workCell: (v) => isNil(v),
    },
  });

  const onSubmit = async (rawValues: typeof form.values) => {
    const { workCell } = rawValues as ValidatedFormBody;
    await mapWorkCellTaskTypesAndGroups(
      workCell.workCellId,
      taskTypes.length ? taskTypes.map((t) => t.taskTypeId) : undefined,
      taskTypeGroups.length ? taskTypeGroups.map((g) => g.taskTypeGroupId) : undefined,
    ).then(() => {
      const message =
        taskTypes.length > 0 && taskTypeGroups.length > 0
          ? `Mapped ${taskTypes.length} task${taskTypes.length === 1 ? '' : 's'} and ${taskTypeGroups.length} task group${taskTypeGroups.length === 1 ? '' : 's'} to ${workCell.workCellName}`
          : taskTypes.length > 0
            ? `Mapped ${taskTypes.length} task${taskTypes.length === 1 ? '' : 's'} to ${workCell.workCellName}`
            : `Mapped ${taskTypeGroups.length} task group${taskTypeGroups.length === 1 ? '' : 's'} to ${workCell.workCellName}`;
      notifications.show({
        title: 'Successfully mapped',
        message,
        color: 'green',
      });
      expandDetailGrid(workCell.workCellId, 'Tasks');
      refreshDetailGrid(workCell.workCellId, 'Tasks');
      onComplete();
    });
  };

  useFormPopover(form, onSubmit);

  return (
    <SearchableSelect
      label="Work Cell"
      paginatedGet={paginatedGet}
      searchKey="workCellName"
      required
      idKey="workCellId"
      getItemLabel={(d) => d.workCellName}
      clearable
      value={form.values.workCell}
      onChange={(v) => form.setFieldValue('workCell', v)}
    />
  );
};

export const MapTaskToWorkCellPopover = ({ taskTypes, taskTypeGroups, onComplete }: Props) => {
  const [opened, setOpened] = useState(false);

  return (
    <FormPopover
      opened={opened}
      onClose={() => setOpened(false)}
      confirmationText="Assign"
      target={
        <Box>
          <Button
            onClick={() => setOpened(true)}
            variant="light"
            leftSection={<EvolveIcon icon="Pin" size="sm" />}
            disabled={taskTypes.length === 0 && taskTypeGroups.length === 0}
          >
            Assign to work cell
          </Button>
        </Box>
      }
    >
      <MapTaskFields taskTypes={taskTypes} taskTypeGroups={taskTypeGroups} onComplete={onComplete} />
    </FormPopover>
  );
};
